<template>
  <div v-if="!loadError" ref="root" id="root"></div>
  <div v-else class="error-container">
    <h1>Error loading remote module</h1>
    <p>{{ errorMessage }}</p>
    <button @click="forceReload">Reload</button>
  </div>
</template>

<script setup>
import { onMounted, ref } from 'vue';
import { useAuthStore } from '../stores/auth';
const piniaAuthStore = useAuthStore();

const props = defineProps({ module: String });
const root = ref(null);
const remoteFrontApp = import.meta.env.VITE_REMOTE_FRONT_APP+'assets/remoteEntry.js';
const loadError = ref(false);

const isDarkMode = ref(false);

const isRootElExists = ref(false);
const rootEl = ref(null);


onMounted(() => {
  const saved = localStorage.getItem("theme"); // "dark" | "light"
  isDarkMode.value = saved ? saved === "dark" : window.matchMedia("(prefers-color-scheme: dark)").matches;
  const rootEl = document.getElementById("root");
  if (rootEl) {
    isRootElExists.value = true;
    rootEl.value = rootEl;
    rootEl.classList.toggle("dark", isDarkMode.value);
  }
});

const forceReload = () => {
  window.location.reload();
}

const getModuleLoader = (moduleName) => {
  const loaders = {
    'Login': () => import('remote_broggl_v2/Login'),
    'RequestNewPassword': () => import('remote_broggl_v2/RequestNewPassword'),
    'ResetPassword': () => import('remote_broggl_v2/ResetPassword'),
    'ClientPage': () => import('remote_broggl_v2/ClientPage'),
    'BAsServiceDeliveryOKRPage': () => import('remote_broggl_v2/BAsServiceDeliveryOKRPage'),
    'Home': () => import('remote_broggl_v2/Home'),
    'Accounts': () => import('remote_broggl_v2/Accounts'),
  };
  return loaders[moduleName];
};

const loadRemoteModule = async (moduleName, mountFn) => {
  const loader = getModuleLoader(moduleName);
  if (!loader) {
    throw new Error(`Unknown remote module: ${moduleName}`);
  }
  try {
    // const { default: mountFn } = await import(`${remoteFrontApp}/${moduleName}`);
    const { default: mountFn } = await loader();
    return mountFn;
  } catch (err) {

    console.error(`Failed to load remote module ${moduleName}:`, err);
    await new Promise(resolve => setTimeout(resolve, 1000));
    try {
      const { default: mountFn } = await loader();
      return mountFn;
    } catch (retryErr) {
      console.error(`Retry failed for ${moduleName}:`, retryErr);
      throw retryErr;
    }
  }
}
onMounted(async () => {
  // let mountFn;
  switch (props.module) {
    case 'Login':
      // const { default: mountFnLogin } = await import('remote_broggl_v2/Login');
      const mountFnLogin = await loadRemoteModule('Login');
      try {
        // Mount directly without Shadow DOM to preserve CSS-in-JS (MUI/Emotion) injection
        const mountDiv = root.value;
        const urlParams = new URLSearchParams(window.location.search);
        const redirectTo = urlParams.get('redirect') || '/';
        mountFnLogin({
          rootElement: mountDiv,
          props: {
            redirectTo,
            navigate: (path) => {
              window.location.href = path;
            },
            onAuthChange: authState => {
              if (authState.isAuthenticated) {
                piniaAuthStore.setUser(authState.user);
                piniaAuthStore.setToken(authState.token);
                piniaAuthStore.setIsAuthenticated(true);
                window.$cookies.set("jwt", authState.token, "8h", { expires: 8 });
                window.$cookies.set("jwt_broggl", authState.token, "8h", { expires: 8 });
                window.$cookies.set("user", authState.user, "8h", { expires: 8 });
              } else {
                piniaAuthStore.setUser(null);
                piniaAuthStore.setToken(null);
                piniaAuthStore.setIsAuthenticated(false);
                window.$cookies.remove("jwt");
                window.$cookies.remove("jwt_broggl");
                window.$cookies.remove("user");
              }
            }
          }
        });
      } catch (err) {
        console.error('Remote mount error:', err.message, err.stack)
      }
      break;
    case 'RequestNewPassword':
      // const { default: mountFnRequestNewPassword } = await import('remote_broggl_v2/RequestNewPassword');
      const mountFnRequestNewPassword = await loadRemoteModule('RequestNewPassword');
      try {
        const mountDiv = root.value;
        mountFnRequestNewPassword({
          rootElement: mountDiv,
          props: {
            navigate: (path) => {
              window.location.href = path;
            }
          }
        });
      } catch (err) {
        console.error('Remote mount error:', err.message, err.stack)
      }
      break;
    case 'ResetPassword':
      // const { default: mountFnResetPassword } = await import('remote_broggl_v2/ResetPassword');
      const mountFnResetPassword = await loadRemoteModule('ResetPassword');
      try {
        const mountDiv = root.value;
        mountFnResetPassword({
          rootElement: mountDiv,
          props: {
            navigate: (path) => {
              window.location.href = path;
            }
          }
        });
      } catch (err) {
        console.error('Remote mount error:', err.message, err.stack)
      }
      break;
    case 'ClientPage':
      // const { default: mountFnClientPage } = await import('remote_broggl_v2/ClientPage');
      const mountFnClientPage = await loadRemoteModule('ClientPage');
      try {
        const mountDiv = root.value;
        mountFnClientPage({
          rootElement: mountDiv,
          props: {
            navigate: (path) => {
              window.location.href = path;
            }
          }
        });
      } catch (err) {
        console.error('Remote mount error:', err.message, err.stack)
      }
      break;
    case 'BAsServiceDeliveryOKRPage':
      // const {default: mountFnServiceDeliveryOKRPage} = await import('remote_broggl_v2/BAsServiceDeliveryOKRPage');
      const mountFnServiceDeliveryOKRPage = await loadRemoteModule('BAsServiceDeliveryOKRPage');
      try {
        const mountDiv = root.value;
        mountFnServiceDeliveryOKRPage({
          rootElement: mountDiv,
          props: {
            navigate: (path) => {
              window.location.href = path
            }
          }
        })
      } catch (err) {
        console.error(err)
      }
      break;
    case 'Home':
      // const { default: mountFnHome } = await import('remote_broggl_v2/Home');
      const mountFnHome = await loadRemoteModule('Home');
      try {
        const mountDiv = root.value;
        mountFnHome({
          rootElement: mountDiv,
          props: {
            navigate: (path) => {
              window.location.href = path
            }
          }
        })
      } catch (err) {
        console.error(err)
      }
      break;
    case 'Accounts':
      // const { default: mountFnAccounts } = await import('remote_broggl_v2/Accounts');
      const mountFnAccounts = await loadRemoteModule('Accounts');
      try {
        const mountDiv = root.value;
        mountFnAccounts({
          rootElement: mountDiv,
          props: {
            navigate: (path) => {
              window.location.href = path
            }
          }
        })
      } catch (err) {
        console.error(err)
      }
      break;
    default:
      throw new Error('Unknown remote module: ' + props.module);
  }
});
</script>

<style lang="scss">
#root {
  transition:
    color 10s ease-in-out,
    background-color 10s ease-in-out,
    border-color 10s ease-in-out,
    box-shadow 10s ease-in-out;

}
</style>
